import { useState } from "react";
import { useOutletContext, useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Workspace } from "../lib/api";
import { LoadingButton } from "../components/Spinner";
import { EnvDot } from "./Environments";

// Collection runner — picks a collection + environment + executor, runs each
// request in order through the existing /api/requests/:id/run endpoint, and
// posts a collection_run_items row per request to /api/collection-runs/:id/items.

export default function Runner() {
  const { workspace } = useOutletContext<{ workspace: Workspace }>();
  const { runId } = useParams();
  const qc = useQueryClient();

  const [collectionId, setCollectionId] = useState<string>("");
  const [envId, setEnvId] = useState<string>("");
  const [agentId, setAgentId] = useState<string>("");
  const [executor, setExecutor] = useState<"worker" | "agent">("worker");
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; current?: string }>({ done: 0, total: 0 });
  const [activeRunId, setActiveRunId] = useState<string | null>(runId ?? null);

  const collections = useQuery({
    queryKey: ["collections", workspace.id],
    queryFn: () => api.collections.list(workspace.id),
  });
  const envs = useQuery({
    queryKey: ["environments", workspace.id],
    queryFn: () => api.environments.list(workspace.id),
  });
  const agents = useQuery({
    queryKey: ["agents", workspace.id],
    queryFn: () => api.agents.list(workspace.id),
  });

  const runHistory = useQuery({
    queryKey: ["collection-runs", collectionId],
    queryFn: () => api.collectionRuns.list(collectionId),
    enabled: !!collectionId,
  });

  const runDetail = useQuery({
    queryKey: ["collection-run", activeRunId],
    queryFn: () => api.collectionRuns.get(activeRunId!),
    enabled: !!activeRunId,
  });

  const startRun = useMutation({
    mutationFn: async () => {
      const colReqs = (await api.collections.requests(collectionId)).requests;
      const run = await api.collectionRuns.start({
        collection_id: collectionId,
        env_id: envId || null,
        agent_id: executor === "agent" ? (agentId || null) : null,
        executor,
      });
      setActiveRunId(run.id);
      setProgress({ done: 0, total: colReqs.length });
      for (let i = 0; i < colReqs.length; i++) {
        const r = colReqs[i];
        setProgress({ done: i, total: colReqs.length, current: r.name });
        const t0 = Date.now();
        try {
          const result = await api.requests.run(r.id, {
            executor,
            agentId: executor === "agent" && agentId ? agentId : undefined,
            environmentId: envId || undefined,
            collectionRunId: run.id,
          });
          await api.collectionRuns.addItem(run.id, {
            request_id: r.id,
            ordinal: i,
            status: result.error ? "error" : (result.status && result.status >= 200 && result.status < 400) ? "pass" : "fail",
            duration_ms: result.durationMs,
            assertions: [],
            request_run_id: result.runId,
          });
        } catch (e: unknown) {
          await api.collectionRuns.addItem(run.id, {
            request_id: r.id,
            ordinal: i,
            status: "error",
            duration_ms: Date.now() - t0,
            assertions: [{ name: "execution", status: "fail", message: String((e as Error).message) }],
          });
        }
      }
      await api.collectionRuns.finish(run.id);
      qc.invalidateQueries({ queryKey: ["collection-runs", collectionId] });
      qc.invalidateQueries({ queryKey: ["collection-run", run.id] });
      return run.id;
    },
    onSuccess: () => setRunning(false),
    onError: () => setRunning(false),
  });

  const activeEnv = (envs.data?.environments ?? []).find((e) => e.id === envId);

  return (
    <div className="h-full overflow-auto scroll-pane">
      {activeEnv?.color && (
        <div className="h-1 w-full" style={{ background: activeEnv.color }} aria-label={`Active environment: ${activeEnv.name}`} />
      )}
      <div className="p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-3">
            Collection runner
            {activeEnv && (
              <span className="inline-flex items-center gap-1.5 text-sm font-normal muted">
                <EnvDot color={activeEnv.color} size={10} /> {activeEnv.name}
              </span>
            )}
          </h1>
          <p className="text-sm muted">Run every request in a collection sequentially. Status per request, totals, and durations get saved as a run record.</p>
        </div>

        <form
          className="card p-5 space-y-3"
          onSubmit={(e) => { e.preventDefault(); setRunning(true); startRun.mutate(); }}
        >
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Collection</label>
              <select className="input" value={collectionId} onChange={(e) => setCollectionId(e.target.value)} required>
                <option value="">— pick one —</option>
                {(collections.data?.collections ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Environment</label>
              <select className="input" value={envId} onChange={(e) => setEnvId(e.target.value)}>
                <option value="">— none —</option>
                {(envs.data?.environments ?? []).map((e) => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Executor</label>
              <select className="input" value={executor} onChange={(e) => setExecutor(e.target.value as "worker" | "agent")}>
                <option value="worker">Worker (Cloudflare edge)</option>
                <option value="agent">Local agent</option>
              </select>
            </div>
            {executor === "agent" && (
              <div>
                <label className="label">Agent</label>
                <select className="input" value={agentId} onChange={(e) => setAgentId(e.target.value)} required>
                  <option value="">— pick one —</option>
                  {(agents.data?.agents ?? []).filter((a) => a.online).map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
                {(agents.data?.agents ?? []).filter((a) => a.online).length === 0 && (
                  <p className="text-xs muted-strong mt-1">
                    No agents connected. <Link to="/agents" className="accent-link">Register one</Link>.
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            <LoadingButton type="submit" className="btn-primary" pending={running} pendingLabel={progress.current ? `Running ${progress.current}… (${progress.done}/${progress.total})` : "Starting…"}>
              Run collection
            </LoadingButton>
            {running && progress.total > 0 && (
              <span className="text-sm muted">{progress.done}/{progress.total} done</span>
            )}
          </div>
        </form>

        {activeRunId && runDetail.data && (
          <div className="card p-5 space-y-3">
            <h2 className="font-semibold">Run results</h2>
            <RunSummary items={runDetail.data.items} />
          </div>
        )}

        {collectionId && (runHistory.data?.runs ?? []).length > 0 && (
          <div className="card p-5 space-y-3">
            <h2 className="font-semibold">Recent runs</h2>
            <ul className="space-y-1 text-sm">
              {(runHistory.data?.runs ?? []).map((r) => {
                const totals = (() => { try { return JSON.parse(r.totals); } catch { return null; } })();
                return (
                  <li key={r.id}>
                    <button className={"w-full text-left rounded-md p-2 hover:bg-surface-2 " + (r.id === activeRunId ? "bg-accent-soft" : "")} onClick={() => setActiveRunId(r.id)}>
                      <span className="font-mono text-xs muted-strong">{new Date(r.started_at).toLocaleString()}</span>
                      {" — "}
                      <span className="chip bg-surface-3 muted-strong">{r.status}</span>
                      {totals && (
                        <span className="ml-2 muted">
                          {totals.passed ?? 0}/{totals.requests ?? 0} passed · {totals.duration_ms ?? 0}ms
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

function RunSummary({ items }: { items: Array<{ ordinal: number; status: string; duration_ms: number; assertions: string; request_name: string; method: string; url: string }> }) {
  return (
    <ul className="space-y-1">
      {items.map((it) => (
        <li key={it.ordinal} className="flex items-center gap-3 rounded-md border border-border bg-surface-2 p-2.5 text-sm">
          <span className={"chip " + (it.status === "pass" ? "bg-good/15 text-good" : it.status === "fail" ? "bg-warn/15 text-warn" : "bg-danger/15 text-danger")}>{it.status}</span>
          <span className={"method-tag " + it.method.toLowerCase()}>{it.method}</span>
          <span className="flex-1 truncate">{it.request_name}</span>
          <span className="text-xs muted mono">{it.duration_ms}ms</span>
        </li>
      ))}
    </ul>
  );
}
